import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import { useLanguage } from '../../../hooks/useLanguage';
import { useOasisBackend } from '../../../hooks/useOasisBackend';

interface PrefillTemplate {
  topic: string;
  subject: string;
  message: string;
}

const PREFILL_TEMPLATES: Record<string, PrefillTemplate> = {
  pilot: {
    topic: 'pilot',
    subject: 'Pilot inquiry',
    message: 'Hi, we are exploring a pilot in …\n\nThe problem we want to solve:\n— …\n\nKind regards,\n[your name]',
  },
  missionos: {
    topic: 'missionos',
    subject: 'MissionOS inquiry',
    message:
      'Hi, we would like to know more about MissionOS for our region.\n\nOur region / organisation: …\nWhat we want to find out: …\n\nKind regards,\n[your name]',
  },
  'missionos-demo': {
    topic: 'missionos',
    subject: 'MissionOS: demo request',
    message:
      'Hi, we would like a demo of MissionOS.\n\nOur region / organisation: …\nWho should attend the demo: …\nPreferred date(s): …\n\nKind regards,\n[your name]',
  },
  'missionos-api': {
    topic: 'missionos',
    subject: 'MissionOS: API-only inquiry',
    message:
      'Hi, we are interested in the MissionOS API-only model: integrating your AI intelligence and Vector Ontology into our own platform.\n\nOur organisation: …\nOur existing platform: …\nWhat we want to integrate: …\n\nKind regards,\n[your name]',
  },
  'missionos-turnkey': {
    topic: 'missionos',
    subject: 'MissionOS: full implementation inquiry',
    message:
      'Hi, we are interested in the MissionOS full, turn-key implementation: software, training and integration.\n\nOur region / organisation: …\nCurrent setup: …\nTimeline we are considering: …\n\nKind regards,\n[your name]',
  },
  'vmo-intake': {
    topic: 'pilot',
    subject: 'VraagMijnOverheid: intake',
    message:
      'Hallo, wij willen graag verkennen of VraagMijnOverheid bij ons ministerie past.\n\nOns ministerie / onderdeel: …\nHuidig Woo-volume (verzoeken per jaar, indicatie): …\nWat we willen oplossen: …\nGewenste startperiode: …\n\nMet vriendelijke groet,\n[je naam]',
  },
  dpa: {
    topic: 'dpa',
    subject: 'DPA request',
    message: 'Hi, hereby I request your DPA because …\n\nKind regards,\n[your name]',
  },
  subprocessors: {
    topic: 'subprocessors',
    subject: 'Sub-processor list request',
    message: 'Hi, please share the current Thoughtful Oasis sub-processor list.\n\nKind regards,\n[your name]',
  },
  brand: {
    topic: 'press',
    subject: 'Brand asset request',
    message:
      'Hi, I would like the following brand asset(s):\n— …\n\nFor: [press / partner page / programme / other]\n\nKind regards,\n[your name]',
  },
  press: {
    topic: 'press',
    subject: 'Press inquiry',
    message: 'Hi, I am writing about Thoughtful Oasis for …\n\nI would like to ask about:\n— …\n\nKind regards,\n[your name]',
  },
  'paper-limit': {
    topic: 'general',
    subject: 'LIMIT counter-example preprint',
    message:
      'Hi, I would like a copy of the preprint "Embedding Dimension Scales with Domain, Not Corpus" (the LIMIT counter-example).\n\nKind regards,\n[your name]',
  },
  'pattern-dataquality': {
    topic: 'pilot',
    subject: 'Partner inquiry: Data quality at the source',
    message:
      "Hi, your 'Data quality at the source' pattern looks like our use case.\n\nOur organisation does: …\nWe want to achieve: …\nSo your pattern would help us: …\n\nKind regards,\n[your name]",
  },
  'pattern-compliance': {
    topic: 'pilot',
    subject: 'Partner inquiry: Compliance and regulatory navigation',
    message:
      "Hi, your 'Compliance and regulatory navigation' pattern looks like our use case.\n\nOur organisation does: …\nWe want to achieve: …\nSo your pattern would help us: …\n\nKind regards,\n[your name]",
  },
  'pattern-tradeoff': {
    topic: 'pilot',
    subject: 'Partner inquiry: Trade-off navigation',
    message:
      "Hi, your 'Trade-off navigation' pattern looks like our use case.\n\nOur organisation does: …\nWe want to achieve: …\nSo your pattern would help us: …\n\nKind regards,\n[your name]",
  },
  'fda-510k': {
    topic: 'pilot',
    subject: 'oasis_0 Asclepius 510k: early access',
    message:
      'Hi, we are interested in early access to oasis_0 Asclepius 510k (510(k) predicate search & substantial equivalence).\n\nOur device family / class: …\nSubmission volume (per year, indication): …\nWhere predicate or equivalence work costs us most time: …\n\nKind regards,\n[your name]',
  },
  'models-notify': {
    topic: 'general',
    subject: 'oasis_0: notify me about new models',
    message:
      'Hi, please keep me posted on new oasis_0 models.\n\nThe domain / decision I care about: …\nMy organisation: …\n\nKind regards,\n[your name]',
  },
};

const Contact: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';
  const location = useLocation();
  const db = useOasisBackend();

  const params = new URLSearchParams(location.search);
  const prefillKey = params.get('prefill') || '';
  const t = PREFILL_TEMPLATES[prefillKey];

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [org, setOrg] = React.useState('');
  const [topic, setTopic] = React.useState(t?.topic || '');
  const [message, setMessage] = React.useState(t?.message || '');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const subject = t?.subject || 'Contact form';

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedOrg = org.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedOrg || !topic || !trimmedMessage) {
      toast.error(
        en
          ? 'Please fill in all required fields.'
          : 'Vul alle verplichte velden in.'
      );
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error(
        en
          ? 'Please enter a valid email address.'
          : 'Voer een geldig e-mailadres in.'
      );
      return;
    }

    // Pack subject/topic/phone/source as a header on the message body so the
    // recipient sees the same context the form captures. `role` is required by
    // the ContactUs endpoint but isn't a field we ask for here.
    const meta = [
      `Subject: ${subject}`,
      `Topic: ${topic}`,
      `Organisation: ${trimmedOrg}`,
      phone ? `Phone: ${phone}` : '',
      prefillKey ? `Source: contact prefill "${prefillKey}"` : '',
      `Language: ${en ? 'en' : 'nl'}`,
    ]
      .filter(Boolean)
      .join('\n');
    const body = `${meta}\n\n${trimmedMessage}`;

    setSubmitting(true);
    try {
      const response = await db.endpoints.api.apiContactUsCreate({
        email: trimmedEmail,
        message: body,
        name: trimmedName,
        company: trimmedOrg,
        role: 'field removed',
      });
      if (response.status !== 201) {
        toast.error(
          en
            ? 'Something went wrong. Please try again.'
            : 'Er ging iets mis. Probeer het opnieuw.'
        );
        return;
      }
      setSubmitted(true);
      toast.success(
        en
          ? "Message sent. We'll be in touch."
          : 'Bericht verzonden. We nemen contact op.'
      );
      setName('');
      setEmail('');
      setPhone('');
      setOrg('');
      setTopic(t?.topic || '');
      setMessage(t?.message || '');
    } catch (err) {
      toast.error(
        en
          ? 'Network error. Please try again.'
          : 'Netwerkfout. Probeer het opnieuw.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WebsiteLayout hideCta hidePartners>
      <Helmet>
        <title>Contact · Thoughtful Oasis</title>
        <meta
          name="description"
          content="Tell us what you're working on. We work with a small number of partners at a time, on problems that matter to them and to us."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="Tell us what you're working on. We work with a small number of partners at a time, on problems that matter to them and to us."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/contact" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "url": "https://thoughtful-oasis.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "Thoughtful Oasis",
              "email": "rens@thoughtful-oasis.com",
              "contactPoint": [{
                "@type": "ContactPoint",
                "contactType": "business inquiries",
                "email": "rens@thoughtful-oasis.com",
                "areaServed": ["NL", "EU"]
              }]
            }
          }
        `}</script>
      </Helmet>

      <section className="contact-split">
        <Reveal as="aside" className="contact-split__intro">
          <span className="label">Contact</span>
          <h1>
            {en
              ? "Tell us what you're working on."
              : 'Vertel ons waar je mee bezig bent.'}
          </h1>
          <p>
            {en
              ? 'We work with a small number of partners at a time, on problems that matter to them and to us.'
              : 'We werken met een klein aantal partners tegelijk, aan problemen die voor hen en voor ons echt iets betekenen.'}
          </p>
          <p>
            {en
              ? "Send the gist of what you're trying to solve and we'll come back to you. A pilot starts with a snapshot of your data, a domain expert at four hours a week, and a real problem."
              : 'Stuur de kern van wat je wil oplossen en we komen erop terug. Een pilot start met een snapshot van jullie data, een domeinexpert voor een paar uur per week, en een echt probleem.'}
          </p>
          <div className="contact-split__meta">
            <div className="contact-split__meta-item">
              <p className="contact-split__meta-label">
                {en ? 'Direct email' : 'Direct e-mail'}
              </p>
              <p className="contact-split__meta-value">
                <a className="link-inline" href="mailto:rens@thoughtful-oasis.com">
                  rens@thoughtful-oasis.com
                </a>
              </p>
            </div>
            <div className="contact-split__meta-item">
              <p className="contact-split__meta-label">
                {en ? 'Visiting address' : 'Bezoekadres'}
              </p>
              <p className="contact-split__meta-value">
                Enschede · Den Haag · Utrecht · Apeldoorn
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal
          as="form"
          className="contact-form"
          id="contact-form"
          // @ts-ignore — Reveal forwards extra props to the rendered <form>
          onSubmit={onSubmit}
          noValidate
        >
          <input type="hidden" name="subject" value={subject} />

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="c-name">
                {en ? 'Name' : 'Naam'}
              </label>
              <input
                className="contact-form__input"
                type="text"
                id="c-name"
                name="name"
                required
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="c-email">
                {en ? 'Email' : 'E-mail'}
              </label>
              <input
                className="contact-form__input"
                type="email"
                id="c-email"
                name="email"
                required
                placeholder="jane@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label
                className="contact-form__label contact-form__label--optional"
                htmlFor="c-phone"
              >
                {en ? 'Phone' : 'Telefoon'}
              </label>
              <input
                className="contact-form__input"
                type="tel"
                id="c-phone"
                name="phone"
                placeholder="+31 …"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="c-org">
                {en ? 'Organisation' : 'Organisatie'}
              </label>
              <input
                className="contact-form__input"
                type="text"
                id="c-org"
                name="organisation"
                required
                placeholder="Company name"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
              />
            </div>
          </div>

          <div className="contact-form__field">
            <label className="contact-form__label" htmlFor="c-topic">
              {en ? 'How can we help?' : 'Waarmee kunnen we helpen?'}
            </label>
            <select
              className="contact-form__input"
              id="c-topic"
              name="topic"
              required
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            >
              <option value="" disabled>
                {en ? 'Select a topic' : 'Kies een onderwerp'}
              </option>
              <option value="pilot">{en ? 'Start a pilot' : 'Een pilot starten'}</option>
              <option value="missionos">MissionOS</option>
              <option value="general">{en ? 'General inquiry' : 'Algemene vraag'}</option>
              <option value="dpa">{en ? 'DPA request' : 'DPA-verzoek'}</option>
              <option value="subprocessors">
                {en ? 'Sub-processor list' : 'Sub-verwerkerslijst'}
              </option>
              <option value="press">{en ? 'Press & brand' : 'Pers & brand'}</option>
              <option value="other">{en ? 'Other' : 'Anders'}</option>
            </select>
          </div>

          <div className="contact-form__field">
            <label className="contact-form__label" htmlFor="c-message">
              {en ? 'Message' : 'Bericht'}
            </label>
            <textarea
              className="contact-form__textarea"
              id="c-message"
              name="message"
              required
              placeholder={
                en
                  ? "Tell us what you're working on, what you'd like to find out, or what you'd like us to send."
                  : 'Vertel waar je mee bezig bent, wat je wil uitvinden, of wat we je kunnen toesturen.'
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <p className="contact-form__notice">
            {en ? (
              <>
                By submitting this form you agree to our{' '}
                <Link className="link-inline" to="/privacy-policy">
                  privacy policy
                </Link>
                . Your data is safe with us.
              </>
            ) : (
              <>
                Door dit formulier in te dienen ga je akkoord met ons{' '}
                <Link className="link-inline" to="/privacy-policy">
                  privacybeleid
                </Link>
                . Je gegevens zijn bij ons in goede handen.
              </>
            )}
          </p>

          <button
            className="btn btn-primary contact-form__submit"
            type="submit"
            disabled={submitting}
          >
            {submitting
              ? en
                ? 'Sending…'
                : 'Verzenden…'
              : en
                ? 'Send message'
                : 'Bericht versturen'}
          </button>

          {submitted && (
            <p
              className="contact-form__notice"
              style={{ color: 'var(--teal-deep)', marginTop: 12 }}
              role="status"
              aria-live="polite"
            >
              {en
                ? "Thanks — we've got your message and we'll be in touch."
                : 'Dank — we hebben je bericht ontvangen en nemen contact op.'}
            </p>
          )}
        </Reveal>
      </section>
    </WebsiteLayout>
  );
};

export default Contact;
